import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Error } from '@material-ui/icons';
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack';
import { useStore } from '@roc/client-portal-shared/hooks';
import { LeaseRow } from '../util/types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    pdfDocument: {
      width: 'fit-content',
      margin: '0 auto',
    },
    pdfPage: {
      margin: theme.spacing(2),
      boxShadow: '2px 2px 4px #888',
      width: 'fit-content',
    },
    previewBox: {
      height: '67.9vh',
      overflowY: 'auto',
      border: '1px solid #CBCBCB',
      borderRadius: '10px',
      padding: '8px',
      transition: 'all 0.5s ease',
    },
  })
);

interface PreviewLeaseProps {
  lease: LeaseRow;
}

export const PreviewLease = observer(({ lease }: PreviewLeaseProps) => {
  const classes = useStyles();
  const { leaseStore } = useStore();
  const { leasePreviewData } = leaseStore;
  const [numPages, setNumPages] = useState<number | null>(null);

  const isPdf = lease.originalFileName?.toLowerCase().endsWith('.pdf') ?? true;

  useEffect(() => {
    leaseStore.previewLease(lease.leaseId);
    return () => {
      leaseStore.leasePreviewData = null;
    };
  }, [lease.leaseId]);

  if (!leasePreviewData) {
    return (
      <Box
        className={classes.previewBox}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!isPdf) {
    return (
      <Box
        className={classes.previewBox}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
      >
        <Error style={{ marginBottom: '5px', fontSize: '40px' }} />
        <Typography variant="h6" align="center">
          We are currently unable to show a preview of this document
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.previewBox}>
      <Document
        file={{ data: leasePreviewData.data }}
        onLoadSuccess={({ numPages }) => setNumPages(numPages)}
        className={classes.pdfDocument}
      >
        {numPages &&
          [...Array(numPages)].map((_, i) => (
            <Page key={i} className={classes.pdfPage} pageNumber={i + 1} />
          ))}
      </Document>
    </Box>
  );
});
